import { Component, Input, Output, EventEmitter, inject, ElementRef, ChangeDetectorRef, HostListener, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { getIcon } from '../../utils/icons.utils';
import { AlertsService } from 'src/app/service/alerts.service';
import { isMessageRedundantWithFile, isImage, isGifMessage, getMessageMeta } from '../../utils/helpers.utils';

@Component({
  selector: 'app-message-menu',
  standalone: true,
  imports: [CommonModule],
  styleUrls: ['./message-menu.component.css'],
  template: `
@if (isActive) {
<!-- Quick Reactions (Together with dropdown) -->
<div class="message-menu-wrapper"
     [class.menu-above]="isAbove"
     [class.menu-below]="!isAbove"
     [style.transform]="horizontalOffset ? 'translateX(' + horizontalOffset + 'px)' : null">
@if (!isNoLongerMember) {
<div class="quick-reaction-menu" (click)="$event.stopPropagation()">
  @for (
    emoji of (commonReactions?.length ? commonReactions : ['👍', '❤️', '😂', '😮', '🙏']);
    track $index
  ) {
  <span class="quick-reaction-emoji" (click)="
                        onQuickReactionSelect(
                          emoji
                        );
                        $event.stopPropagation()
                      ">{{ emoji }}</span>
  }
  <div class="quick-reaction-plus" (click)="openFullReactionPicker(); $event.stopPropagation()">
    <svg viewBox="0 0 24 24" width="16" height="16">
      <path fill="currentColor" d="M11 11V5h2v6h6v2h-6v6h-2v-6H5v-2h6z"></path>
    </svg>
  </div>
</div>
}

<div class="dropdown-menu three-dot show" [style.max-height]="dropdownMaxHeight">
  @if (isTextMessage()) {
  <button class="dropdown-item" (mousedown)="$event.preventDefault()" (click)="copyText(); $event.stopPropagation()">
    <span class="mr-2" [innerHTML]="internalGetSafeIcon('copy', 'currentColor')"></span>
    {{ getCopyButtonText() }}
  </button>
  }
  @if (isImageMessage()) {
  <button class="dropdown-item" (mousedown)="$event.preventDefault()" (click)="copyImage(); $event.stopPropagation()">
    <span class="mr-2" [innerHTML]="internalGetSafeIcon('copy', 'currentColor')"></span>
    Copy Image
  </button>
  }
  @if (!isNoLongerMember) {
  <button class="dropdown-item" (mousedown)="$event.preventDefault()" (click)="onActionClick('reply'); $event.stopPropagation()">
    <span class="mr-2" [innerHTML]="internalGetSafeIcon('reply', 'currentColor')"></span>
    Reply
  </button>
  }
  @if (isGroupChat && !isCurrentUserSender()) {
  <button class="dropdown-item" (mousedown)="$event.preventDefault()" (click)="onActionClick('replyPrivately'); $event.stopPropagation()">
    <span class="mr-2" [innerHTML]="internalGetSafeIcon('replyPrivately', 'currentColor')"></span>
    Reply privately
  </button>
  }
  <button class="dropdown-item" (mousedown)="$event.preventDefault()" (click)="onActionClick('forward'); $event.stopPropagation()">
    <span class="mr-2" [innerHTML]="internalGetSafeIcon('forward', 'currentColor')"></span>
    Forward
  </button>
  @if (!isNoLongerMember) {
  <button class="dropdown-item" (mousedown)="$event.preventDefault()" (click)="onActionClick('makeTask'); $event.stopPropagation()">
    <span class="mr-2 material-symbols-outlined" style="font-size: 16px; vertical-align: -3px;">add_task</span>
    Make a task
  </button>
  }

  @if (isGroupChat && isCurrentUserSender()) {
  <button class="dropdown-item" (mousedown)="$event.preventDefault()" (click)="onActionClick('seenInfo'); $event.stopPropagation()">
    <span class="mr-2" [innerHTML]="internalGetSafeIcon('seenInfo', 'currentColor')"></span>Seen Info
  </button>
  }
  @if (!isNoLongerMember && isCurrentUserSender() && isTextMessage()) {
  <button class="dropdown-item" (mousedown)="$event.preventDefault()" (click)="onActionClick('edit'); $event.stopPropagation()">
    <span class="mr-2" [innerHTML]="internalGetSafeIcon('edit', 'currentColor')"></span>Edit
  </button>
  }
  @if (!isNoLongerMember && isCurrentUserSender()) {
  <button class="dropdown-item" (mousedown)="$event.preventDefault()" (click)="onActionClick('deleteForEveryone'); $event.stopPropagation()">
    <span class="mr-2" [innerHTML]="internalGetSafeIcon('delete', '#ff4d4f')"></span>Delete for everyone
  </button>
  }
  <button class="dropdown-item" (mousedown)="$event.preventDefault()" (click)="onActionClick('deleteForMe'); $event.stopPropagation()">
    <span class="mr-2" [innerHTML]="internalGetSafeIcon('delete', 'currentColor')"></span>Delete for me
  </button>
  @if (!isNoLongerMember) {
  @if (isPinned) {
  <button class="dropdown-item" (mousedown)="$event.preventDefault()" (click)="onActionClick('unpin'); $event.stopPropagation()">
    <span class="mr-2" [innerHTML]="internalGetSafeIcon('unpin', 'currentColor')"></span>Unpin
  </button>
  } @else {
  <button class="dropdown-item" (mousedown)="$event.preventDefault()" (click)="onActionClick('pin'); $event.stopPropagation()">
    <span class="mr-2" [innerHTML]="internalGetSafeIcon('pin', 'currentColor')"></span>Pin
  </button>
  }
  }
  @if (isStarred) {
  <button class="dropdown-item" (mousedown)="$event.preventDefault()" (click)="onActionClick('unstar'); $event.stopPropagation()">
    <span class="mr-2" style="font-size: 18px; color: #ffbc00;">★</span>Unstar
  </button>
  } @else {
  <button class="dropdown-item" (mousedown)="$event.preventDefault()" (click)="onActionClick('star'); $event.stopPropagation()">
     <span class="mr-2" style="font-size: 18px; color: #ffbc00;">☆</span>Star
  </button>
  }
</div>
</div>
}
`
})
export class MessageMenuComponent implements OnChanges, OnDestroy {
  @Input() message: any;
  @Input() isActive: boolean = false;
  @Input() commonReactions: string[] = ['👍', '❤️', '😂', '😮', '🙏'];
  @Input() isPinned: boolean = false;
  @Input() isStarred: boolean = false;
  @Input() userId: string = '';
  @Input() isGroupChat: boolean = false;
  @Input() isNoLongerMember: boolean = false;

  // Dropdown Positioning State
  isAbove: boolean = false;
  dropdownMaxHeight: string | null = null;
  horizontalOffset: number = 0;

  private rafId: any = null;
  private rafId2: any = null;
  private isListenersAttached: boolean = false;

  private readonly elementRef = inject(ElementRef);
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly alertsService = inject(AlertsService);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isActive']) {
      if (this.isActive) {
        this.onMenuOpened();
      } else {
        this.onMenuClosed();
      }
    }
  }

  ngOnDestroy(): void {
    this.onMenuClosed();
  }

  private onMenuOpened(): void {
    this.estimateInitialPosition();
    this.attachEventListeners();
    this.rafId = requestAnimationFrame(() => {
      this.calculatePosition();
      this.rafId2 = requestAnimationFrame(() => {
        this.calculatePosition();
      });
    });
  }

  private onMenuClosed(): void {
    this.detachEventListeners();
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    if (this.rafId2) {
      cancelAnimationFrame(this.rafId2);
      this.rafId2 = null;
    }
    this.isAbove = false;
    this.dropdownMaxHeight = null;
    this.horizontalOffset = 0;
  }

  private getScrollableContainer(hostEl: HTMLElement): HTMLElement | null {
    return (hostEl.closest('.chat-box-container') ||
      hostEl.closest('.chat-conversation-container') ||
      document.getElementById('singleChatContainer') ||
      document.getElementById('groupChatContainer')) as HTMLElement | null;
  }

  private estimateInitialPosition(): void {
    const hostEl = this.elementRef?.nativeElement as HTMLElement;
    if (!hostEl) return;

    const container = this.getScrollableContainer(hostEl);
    const containerRect = container ? container.getBoundingClientRect() : {
      top: 0,
      bottom: window.innerHeight,
      left: 0,
      right: window.innerWidth
    };

    const triggerEl = (hostEl.closest('.message-options')?.querySelector('.menu-trigger') as HTMLElement) ||
      (hostEl.closest('.message-options') as HTMLElement) ||
      hostEl;

    const triggerRect = triggerEl.getBoundingClientRect();
    const visibleTop = Math.max(containerRect.top, 0);
    const visibleBottom = Math.min(containerRect.bottom, window.innerHeight);

    const estimatedHeight = 330;
    const PADDING = 10;

    const spaceBelow = visibleBottom - triggerRect.bottom - PADDING;
    const spaceAbove = triggerRect.top - visibleTop - PADDING;

    if (spaceBelow < estimatedHeight && spaceAbove > spaceBelow) {
      this.isAbove = true;
    } else {
      this.isAbove = false;
    }
    this.cdRef.markForCheck();
  }

  calculatePosition(): void {
    if (!this.isActive) return;

    const hostEl = this.elementRef?.nativeElement as HTMLElement;
    if (!hostEl) return;

    const container = this.getScrollableContainer(hostEl);
    const containerRect = container ? container.getBoundingClientRect() : {
      top: 0,
      bottom: window.innerHeight,
      left: 0,
      right: window.innerWidth,
      width: window.innerWidth,
      height: window.innerHeight
    };

    const triggerEl = (hostEl.closest('.message-options')?.querySelector('.menu-trigger') as HTMLElement) ||
      (hostEl.closest('.message-options') as HTMLElement) ||
      hostEl;

    const triggerRect = triggerEl.getBoundingClientRect();
    const visibleTop = Math.max(containerRect.top, 0);
    const visibleBottom = Math.min(containerRect.bottom, window.innerHeight);
    const visibleLeft = Math.max(containerRect.left, 0);
    const visibleRight = Math.min(containerRect.right, window.innerWidth);

    if (triggerRect.bottom < visibleTop - 20 || triggerRect.top > visibleBottom + 20) {
      this.closeMenu.emit();
      return;
    }

    const menuWrapper = hostEl.querySelector('.message-menu-wrapper') as HTMLElement;
    const dropdownMenu = hostEl.querySelector('.dropdown-menu.three-dot') as HTMLElement;
    const quickReaction = hostEl.querySelector('.quick-reaction-menu') as HTMLElement;

    const dropdownHeight = dropdownMenu ? (dropdownMenu.scrollHeight || dropdownMenu.offsetHeight || 280) : 280;
    const reactionHeight = quickReaction ? (quickReaction.offsetHeight || 36) : 36;
    const totalMenuHeight = dropdownHeight + reactionHeight + 14;

    const PADDING = 10;

    const spaceBelow = visibleBottom - triggerRect.bottom - PADDING;
    const spaceAbove = triggerRect.top - visibleTop - PADDING;

    let newIsAbove = false;
    if (spaceBelow < totalMenuHeight) {
      if (spaceAbove >= totalMenuHeight || spaceAbove > spaceBelow) {
        newIsAbove = true;
      }
    }

    this.isAbove = newIsAbove;

    const availableSpace = newIsAbove ? spaceAbove : spaceBelow;
    if (availableSpace < totalMenuHeight) {
      const allowedDropdownHeight = Math.max(120, Math.floor(availableSpace - reactionHeight - 20));
      this.dropdownMaxHeight = `${allowedDropdownHeight}px`;
    } else {
      this.dropdownMaxHeight = null;
    }

    if (menuWrapper) {
      menuWrapper.style.transform = '';
      const menuRect = menuWrapper.getBoundingClientRect();
      let offset = 0;

      if (menuRect.right > visibleRight - 8) {
        offset = (visibleRight - 8) - menuRect.right;
      } else if (menuRect.left < visibleLeft + 8) {
        offset = (visibleLeft + 8) - menuRect.left;
      }

      this.horizontalOffset = Math.round(offset);
      if (this.horizontalOffset !== 0) {
        menuWrapper.style.transform = `translateX(${this.horizontalOffset}px)`;
      }
    }

    this.cdRef.markForCheck();
  }

  private boundScrollListener = () => {
    if (this.isActive) {
      this.calculatePosition();
    }
  };

  private boundResizeListener = () => {
    if (this.isActive) {
      this.calculatePosition();
    }
  };

  private attachEventListeners(): void {
    if (this.isListenersAttached) return;
    window.addEventListener('scroll', this.boundScrollListener, { capture: true, passive: true });
    window.addEventListener('resize', this.boundResizeListener, { passive: true });
    this.isListenersAttached = true;
  }

  private detachEventListeners(): void {
    if (!this.isListenersAttached) return;
    window.removeEventListener('scroll', this.boundScrollListener, { capture: true } as any);
    window.removeEventListener('resize', this.boundResizeListener);
    this.isListenersAttached = false;
  }

  @HostListener('document:selectionchange')
  onSelectionChange() {
    if (this.isActive) {
      this.cdRef.markForCheck();
      this.cdRef.detectChanges();
    }
  }

  getCopyButtonText(): string {
    if (this.getSelectedText()) {
      return 'Copy Selected Text';
    }
    return this.isImageMessage() ? 'Copy Text' : 'Copy';
  }

  getSelectedText(): string {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
      return '';
    }

    const text = selection.toString();
    if (!text || !text.trim()) {
      return '';
    }

    const hostEl = this.elementRef?.nativeElement as HTMLElement;
    if (!hostEl) return '';

    const messageEl = hostEl.closest('.message-item-wrapper') ||
      hostEl.closest('.bubble-container') ||
      hostEl.closest('.message-content-box');
    if (!messageEl) return '';

    const anchor = selection.anchorNode;
    const focus = selection.focusNode;
    if (!anchor || !focus) return '';

    // Verify both anchor and focus nodes are inside this message element
    if (!messageEl.contains(anchor) || !messageEl.contains(focus)) {
      return '';
    }

    // Exclude selections that are inside the message menu itself
    const menuEl = hostEl.querySelector('.message-menu-wrapper') || hostEl;
    if (menuEl.contains(anchor) || menuEl.contains(focus)) {
      return '';
    }

    return text;
  }

  internalGetSafeIcon(type: string, color: string): SafeHtml {
    try {
      const icon = getIcon(type as any, color, '18px');
      return this.sanitizer.bypassSecurityTrustHtml(icon || '');
    } catch (e) {
      return '';
    }
  }

  @Output() reply = new EventEmitter<any>();
  @Output() replyPrivately = new EventEmitter<any>();
  @Output() forward = new EventEmitter<any>();
  @Output() edit = new EventEmitter<any>();
  @Output() deleteForEveryone = new EventEmitter<any>();
  @Output() deleteForMe = new EventEmitter<any>();
  @Output() pinMessage = new EventEmitter<any>();
  @Output() unpinMessage = new EventEmitter<any>();
  @Output() starMessage = new EventEmitter<any>();
  @Output() unstarMessage = new EventEmitter<any>();
  @Output() quickReactionSelect = new EventEmitter<{ emoji: string; messageId: string }>();
  @Output() openReactionPicker = new EventEmitter<string>();
  @Output() seenInfo = new EventEmitter<any>();
  @Output() closeMenu = new EventEmitter<void>();
  /** Tasks (14 Oct 2026): turn this message into a task. */
  @Output() makeTask = new EventEmitter<any>();

  isCurrentUserSender(): boolean {
    if (!this.message || !this.userId) {
      return false;
    }
    const senderId = this.message?.sender_id || this.message?.senderId || this.message?.user_id || this.message?.sid;
    return String(senderId) === String(this.userId);
  }

  onQuickReactionSelect(emoji: string) {
    if (!this.message) return;
    this.closeMenu.emit();
    const messageId = this.message._id || this.message.id || this.message.messageId;
    this.quickReactionSelect.emit({ emoji, messageId });
  }

  openFullReactionPicker() {
    if (!this.message) return;
    this.closeMenu.emit();
    const messageId = this.message._id || this.message.id || this.message.messageId;
    this.openReactionPicker.emit(messageId);
  }

  isTextMessage(): boolean {
    const text = this.message?.meta?.caption || this.message?.message;
    if (!text) return false;
    // Don't show text copy if the text is just the filename of the attachment
    return !isMessageRedundantWithFile(this.message);
  }

  private getImageUrl(): string | null {
    if (!this.message) return null;
    const meta = getMessageMeta(this.message);

    // Check various common fields for image URLs in this project
    const candidates = [
      this.message.url,
      meta.fileUrl,
      meta.url,
      meta.gifUrl,
      this.message.message, // Sometimes the text itself is the URL
      ...(this.message.files?.map((f: any) => f.fileUrl) || []),
      ...(meta.files?.map((f: any) => f.fileUrl) || [])
    ];

    for (const url of candidates) {
      const actualUrl = Array.isArray(url) ? url[0] : url;
      if (actualUrl && typeof actualUrl === 'string' && (isImage(actualUrl) || isGifMessage(this.message))) {
        return actualUrl;
      }
    }

    return null;
  }

  isImageMessage(): boolean {
    return !!this.getImageUrl();
  }

  async copyText() {
    const selected = this.getSelectedText();
    const fullText = this.message?.meta?.caption || this.message?.message;
    const textToCopy = selected || fullText;
    if (!textToCopy) return;
    try {
      this.closeMenu.emit();
      await navigator.clipboard.writeText(textToCopy);
      this.alertsService.sendAlert(selected ? 'Selected text copied to clipboard' : 'Text copied to clipboard', true);
    } catch (err) {
      console.error('Failed to copy text: ', err);
      try {
        const textarea = document.createElement('textarea');
        textarea.value = textToCopy;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        this.alertsService.sendAlert(selected ? 'Selected text copied to clipboard' : 'Text copied to clipboard', true);
      } catch (fallbackErr) {
        console.error('Fallback copy failed: ', fallbackErr);
      }
    }
  }

  async copyImage() {
    const imageUrl = this.getImageUrl();
    if (!imageUrl) return;
    try {
      this.closeMenu.emit();
      // this.alertsService.sendAlert('Copying image...', true);

      const response = await fetch(imageUrl);
      let blob = await response.blob();

      // Most browsers only support copying PNGs to clipboard
      if (blob.type !== 'image/png') {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.src = imageUrl;
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = () => reject(new Error('Failed to load image for conversion. Ensure CORS is enabled on the media server.'));
        });

        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('Failed to get canvas context');
        ctx.drawImage(img, 0, 0);

        const pngBlob = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, 'image/png'));
        if (!pngBlob) throw new Error('Failed to convert image to PNG');
        blob = pngBlob;
      }

      await navigator.clipboard.write([
        new ClipboardItem({
          [blob.type]: blob
        })
      ]);
      this.alertsService.sendAlert('Image copied to clipboard', true);
    } catch (err) {
      console.error('Failed to copy image: ', err);
      // Fallback: If image copying fails (e.g. CORS), copy the URL but alert the user
      try {
        await navigator.clipboard.writeText(imageUrl);
        this.alertsService.sendAlert('Image URL copied (Data restricted)', true);
      } catch (e) {
        this.alertsService.sendAlert('Failed to copy image', true);
      }
    }
  }

  onActionClick(action: string, data?: any) {
    this.closeMenu.emit();
    switch (action) {
      case 'reply': this.reply.emit(data || this.message); break;
      case 'replyPrivately': this.replyPrivately.emit(data || this.message); break;
      case 'forward': this.forward.emit(data || this.message); break;
      case 'edit': this.edit.emit(data || this.message); break;
      case 'deleteForEveryone': this.deleteForEveryone.emit(data || this.message); break;
      case 'deleteForMe': this.deleteForMe.emit(data || this.message); break;
      case 'pin': this.pinMessage.emit(data || this.message); break;
      case 'unpin': this.unpinMessage.emit(data || this.message); break;
      case 'star': this.starMessage.emit(data || this.message); break;
      case 'unstar': this.unstarMessage.emit(data || this.message); break;
      case 'seenInfo': this.seenInfo.emit(data || this.message); break;
      case 'makeTask': this.makeTask.emit(data || this.message); break;
    }
  }
}
